import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {getAssetUrl} from '@Utils/s3.util';
import {AI_VIDEO_PROMPT_BOX_ID} from '@Panels/ai-video-panel/ai-video-panel.types';
import styles from './ai-video-empty-state.module.scss';
import {AI_VIDEOS_ASSET_PREFIX, EMPTY_AI_VIDEO_INFOGRAPHICS, EMPTY_AI_VIDEO_INFOGRAPHICS_LANDSCAPE} from '@Panels/ai-video-panel/ai-video-panel.library';
import type {AiVideoEmptyStateProps} from './ai-video-empty-state.types';
import {getCompatibleVideoFileExtension} from '@Utils/video.util';
import {noop} from '@Utils/general.util';
import {DEFAULT_VIDEO_EXTENSION} from '@Libraries/ai-video.library';
import {isMobile} from 'react-device-detect';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';

const CYCLE_INTERVAL_MS = 2900;
const TOTAL_PLAY_COUNT = 2;

const EXAMPLE_PROMPT_KEYS = ['pmwjs_ai_video_empty_state_1', 'pmwjs_ai_video_empty_state_2', 'pmwjs_ai_video_empty_state_3'];
const MAX_TEXT_ADVANCES = EXAMPLE_PROMPT_KEYS.length * TOTAL_PLAY_COUNT - 1;

export function AiVideoEmptyState({className = '', ...props}: AiVideoEmptyStateProps): ReactElement {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasInteractedWithOptions, setHasInteractedWithOptions] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoPlayCountRef = useRef(0);
  const isVideoFinishedRef = useRef(false);
  const textAdvancesRef = useRef(0);
  const isTextFinishedRef = useRef(false);
  const {state} = useAiPromptBox(AI_VIDEO_PROMPT_BOX_ID);
  const isPromptEmpty = state.prompt.trim().length === 0;

  useEffect(() => {
    const onDocumentClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (target?.closest(`[data-prompt-box-id="${AI_VIDEO_PROMPT_BOX_ID}"]`)) {
        setHasInteractedWithOptions(true);
      }
    };
    document.addEventListener('click', onDocumentClick, true);
    return (): void => {
      document.removeEventListener('click', onDocumentClick, true);
    };
  }, []);

  useEffect(() => {
    handleTextPauseOnInteraction();
    if (!isPromptEmpty || hasInteractedWithOptions) {
      isTextFinishedRef.current = true;
      return;
    }
    if (isTextFinishedRef.current) {
      return;
    }
    const interval = window.setInterval(() => {
      textAdvancesRef.current += 1;
      setActiveIndex((prev) => (prev + 1) % EXAMPLE_PROMPT_KEYS.length);
      if (textAdvancesRef.current >= MAX_TEXT_ADVANCES) {
        isTextFinishedRef.current = true;
        window.clearInterval(interval);
      }
    }, CYCLE_INTERVAL_MS);
    return (): void => {
      window.clearInterval(interval);
    };
  }, [isPromptEmpty, hasInteractedWithOptions]);

  const handleTextPauseOnInteraction = (): void => {
    const videoEl = videoRef.current;
    if (!videoEl || isVideoFinishedRef.current) {
      return;
    }
    if (isPromptEmpty && !hasInteractedWithOptions) {
      videoEl.play().catch(noop);
    } else {
      videoEl.pause();
      isVideoFinishedRef.current = true;
    }
  }

  const onVideoEnded = (): void => {
    if (props.infographicUrl) {
      return;
    }
    const videoEl = videoRef.current;
    if (!videoEl) {
      return;
    }
    videoPlayCountRef.current += 1;
    if (videoPlayCountRef.current >= TOTAL_PLAY_COUNT) {
      return;
    }
    videoEl.currentTime = 0;
    videoEl.play().catch(noop);
  };

  const onVideoTimeUpdate = (): void => {
    if (props.infographicUrl) {
      return;
    }
    const videoEl = videoRef.current;
    if (!videoEl || isVideoFinishedRef.current) {
      return;
    }
    const isFinalPlay = videoPlayCountRef.current >= TOTAL_PLAY_COUNT - 1;
    const isNearEnd = videoEl.currentTime >= videoEl.duration - 0.6;
    if (isFinalPlay && isNearEnd) {
      isVideoFinishedRef.current = true;
      videoEl.pause();
    }
  };

  const getInfographicUrl = (): string => {
    if (props.infographicUrl) {
      return props.infographicUrl;
    }
    if (props.isFullScreen && !isMobile) {
      return getAssetUrl(`${AI_VIDEOS_ASSET_PREFIX}${EMPTY_AI_VIDEO_INFOGRAPHICS_LANDSCAPE}.${getCompatibleVideoFileExtension(DEFAULT_VIDEO_EXTENSION)}`);
    }
    return getAssetUrl(`${AI_VIDEOS_ASSET_PREFIX}${EMPTY_AI_VIDEO_INFOGRAPHICS}.${getCompatibleVideoFileExtension(DEFAULT_VIDEO_EXTENSION)}`);
  }

  const renderVideoSkeleton = (): ReactElement | null => {
    if (isVideoReady) {
      return null;
    }
    return <SkeletonBrick className={styles.videoSkeleton} radius="8px" theme={SkeletonBrickTheme.LIGHT} />;
  };

  return (
    <div className={`flex-center flex-column ${className}`}>
      <div className={`flex-center ${styles.videoContainer}`}>
        <video
          ref={videoRef}
          src={getInfographicUrl()}
          height={220}
          width={300}
          autoPlay
          muted
          loop={!!props.infographicUrl}
          onCanPlay={(): void => {
            if (videoRef.current) {
              videoRef.current.playbackRate = 0.75;
            }
            setIsVideoReady(true);
          }}
          className={isVideoReady ? '' : '_hidden'}
          onEnded={onVideoEnded}
          onTimeUpdate={onVideoTimeUpdate}
        />
        {renderVideoSkeleton()}
      </div>
      <div className={`flex-center spacing-p-b-4`}>
        <Text
          key={activeIndex}
          className={`ct-neutral-primary-disabled-dark text-center ${props.infographicText ? '' : styles.infographicText} ${!props.infographicText ? styles.fadeIn : ''}`}
          size={TextSize.MD}
          val={props.infographicText ?? window.i18next.t(EXAMPLE_PROMPT_KEYS[activeIndex])}
        />
      </div>
    </div>
  );
}
